'use strict';

/**
 * Site class contains general purpose site-specific features.
 *
 * @version 1.1.0
 */
class Site {

	/**
	 * Class constructor
	 *
	 * @param {Object} options Options for the class.
	 */
	constructor(options = {}) {
		this.options = {};

		// Init polyfills
		const polyfills = new Polyfills();
		polyfills.init();

		// Init off-canvas after mobile menu is ready
		document.addEventListener('boringmenu-init-done', () => {
			const offCanvas = new OffCanvas({
				debug: false,
				overlay: true
			});
		});

		// Init mobile menu
		const mobileMenu = document.getElementById('mobile-menu');
		new boringmenu({
			selectors: {
				menu: '.menu-mobile__list--level-1',
			},
			classes: {
				item: 'menu-mobile__item',
				itemActive: 'menu-mobile__item--current',
				itemParent: 'menu-mobile__item--parent',
				toggle: 'menu-mobile__toggle',
				toggleTextContainer: 'sr-only',
			},
			labels: {
				'menu.open': mobileMenu ? mobileMenu.getAttribute('data-labels-open') : 'open',
				'menu.close': mobileMenu ? mobileMenu.getAttribute('data-labels-close') : 'close',
			},
			icons: {
				'menu.open': 'icon-open',
				'menu.close': 'icon-close',
			},
		});

		// Initialize
		this.init(options);
	}

	/**
	 * Init the class by calling applicable init methods
	 *
	 * @param {Object} options Options for the class.
	 * @return {Object}
	 */
	init(options = {}) {

		// Merge user options to the defaults
		this.options = {
			responsiveTables: {
				selector: 'main table'
			},
			imageLinks: {
				parentSelector: 'main'
			},
			...options
		};

		// Call individual init methods
		this.initResponsiveTables();
		this.initSkipLinks();
		this.initImageLinks();
		this.initPeeker();
		this.initWordLinks();
		this.initEntrySearch();
		this.initListFilters();

		// Dispatch custom event when init is done
		document.dispatchEvent(
			new CustomEvent('site-init-done', {
				bubbles: true,
				cancelable: true
			})
		);

		return this;
	}

	/**
	 * Initialize responsive tables
	 *
	 * Finds content tables and wraps them with div.table-wrapper.
	 */
	initResponsiveTables() {
		document.querySelectorAll(this.options.responsiveTables.selector).forEach(table => {
			if (!table.closest('.table-wrapper')) {
				const tableWrapper = document.createElement('div');
				tableWrapper.classList.add('table-wrapper');
				tableWrapper.classList.add('overflow-x-auto');
				table.parentNode.insertBefore(tableWrapper, table);
				tableWrapper.appendChild(table);
			}
		});
	}

	/**
	 * Initialize skip links
	 *
	 * Finds skip links and enhances their behaviour for various screen readers and mobile devices.
	 */
	initSkipLinks() {
		const skipLinks = document.querySelectorAll('.skip-link:not([data-skip-link])');
		if (skipLinks.length) {
			const skipToBlur = event => {
				if (event.target.getAttribute('data-tabindex')) {
					event.target.removeAttribute('tabindex');
					event.target.removeAttribute('data-tabindex');
				}
			};
			skipLinks.forEach(skipLink => {
				skipLink.setAttribute('data-skip-link', true);
				skipLink.addEventListener('click', event => {
					const skipTo = document.getElementById(event.target.href.split('#')[1]);
					if (skipTo && skipTo.getAttribute('tabindex') != '-1') {
						event.preventDefault();
						skipTo.setAttribute('tabindex', '-1');
						skipTo.setAttribute('data-tabindex', true);
						skipTo.addEventListener('blur', skipToBlur);
						skipTo.addEventListener('focusout', skipToBlur);
						skipTo.focus();
					}
				});
			});
		}
	}

	/**
	 * Initialize image links
	 */
	initImageLinks() {

		// Parent of image links
		let parentNode = document.querySelector(this.options.imageLinks.parentSelector);
		if (!parentNode) return;

		// Add glightbox class to image links
		parentNode.querySelectorAll('a[href$=".jpg"], a[href$=".jpeg"], a[href$=".png"], a[href$=".gif"]').forEach(link => {
			if (!link.classList.contains('glightbox')) {
				if (!link.getAttribute('data-title') && !link.getAttribute('data-glightbox')) {
					let figcaption = link.parentNode.querySelector('figcaption');
					if (figcaption) {
						let caption = figcaption ? figcaption.textContent : '';
						link.setAttribute('data-title', caption);
					}
				}
				link.classList.add('glightbox');
			}
		});

		// Initialize GLightbox
		if (parentNode.querySelector('.glightbox')) {
			GLightbox();
		}
	}

	initPeeker() {

		// Main element
		const mainElement = document.querySelector('.peeker');
		if (!mainElement) return;

		// Initialize peeker
		const peekerCache = {};
		const peeker = new Peeker({
			mainElement: mainElement,
			getContent: function (item, clickHandler, panelContentElement) {
				return new Promise((resolve, reject) => {
					const itemHref = clickHandler.getAttribute('href') + 'ajax/';
					if (Object.prototype.hasOwnProperty.call(peekerCache, itemHref)) {
						return resolve(peekerCache[itemHref]);
					}
					axios({
						method: "get",
						url: itemHref,
						headers: { 'X-Requested-With': 'XMLHttpRequest' },
					}).then(response => {
						peekerCache[itemHref] = response.data;
						return resolve(response.data)
					}).catch(error => {
						return reject('Content could not be loaded!')
					})
				})
			}
		})

		window.addEventListener('peeker-panel-open', (e) => {
			const closeButton = e.detail.panelContentElement.querySelector('.close--word')
			closeButton.addEventListener('click', () => {
				peeker.closePanel()
			})
		})
	}

	/**
	 * Initialize word links
	 */
	initWordLinks() {

		// Word link config is required
		if (!wordLinkConfig || !wordLinkConfig.path) return;

		// Links
		const wordLinks = document.querySelectorAll('.rte [href^="' + wordLinkConfig.path + '"]')
		if (!wordLinks.length) return;

		// Add event listeners
		const wordbox = GLightbox({
			skin: 'word-theme',
			height: 'auto',
			selector: '.word-link'
		});
		const wordCache = {};
		wordLinks.forEach(wordLink => {
			wordLink.addEventListener('click', e => {
				e.preventDefault();
				wordLink.classList.add('is-ajax-loading');
				const itemHref = wordLink.href + 'ajax/';
				if (Object.prototype.hasOwnProperty.call(wordCache, itemHref)) {
					this.openWordBox(wordCache[itemHref], wordLink, wordbox)
				} else {
					axios({
						method: "get",
						url: itemHref,
						headers: { 'X-Requested-With': 'XMLHttpRequest' },
					}).then(response => {
						wordCache[itemHref] = response.data;
						this.openWordBox(wordCache[itemHref], wordLink, wordbox)
					}).catch(error => {
						wordLink.classList.remove('is-ajax-loading');
						console.log('Content could not be loaded!')
					})
				}
			});
		})
	}

	openWordBox(linkContent, termLink, wordbox) {
		wordbox.setElements([
			{
				content: linkContent
			}
		]);
		termLink.classList.remove('is-ajax-loading');
		wordbox.open();
		document.getElementById('glightbox-body').querySelector('button.close--word').addEventListener('click', (e) => {
			e.preventDefault()
			wordbox.close()
		})
	}

	/**
	 * Initialize entry search
	 */
	initEntrySearch() {
		const inputs = document.querySelectorAll('.js-entry-search');
		if (!inputs.length) return;

		const entrySearchLabel = document.getElementById('js-entry-search-label');
		const entrySearchDefaultSort = document.getElementById('c_sort').value;
		const entrySearchRoot = document.getElementById('c_query').getAttribute('data-entry-search-root');
		const entrySearchCache = {
			entrySearchRoot: document.getElementById('js-entry-list').innerHTML,
		};

		let entrySearchSorted = false;
		let entrySearchTimeout = null;
		let entrySearchPreviousQueryURL = null;

		inputs.forEach(input => {
			input.addEventListener(input.nodeName === 'SELECT' || input.type === 'checkbox' ? 'change' : 'keyup', (e) => {
				e.preventDefault;
				window.clearTimeout(entrySearchTimeout);
				let entrySearchTimeOutDelay = 1000;
				if (input.nodeName !== 'INPUT' || (e.keyCode || e.which) === 13) {
					entrySearchTimeOutDelay = 50;
				}
				entrySearchTimeout = window.setTimeout(() => {
					const queryObj = {
						'c_ajax': true,
						'c_query': document.getElementById('c_query').value,
						'c_tags': document.getElementById('c_tags').value,
						'c_sort': document.getElementById('c_sort').value,
					}
					if (
						queryObj.c_query.length < 3
						&& !queryObj.c_tags
						&& queryObj.c_sort == entrySearchDefaultSort && !entrySearchSorted
						&& Object.prototype.hasOwnProperty.call(entrySearchCache, entrySearchRoot)
					) {
						this.entrySearchDone(entrySearchCache, entrySearchLabel, '', entrySearchRoot);
						return;
					}
					let queryURL = entrySearchRoot;
					if (queryObj.c_query || queryObj.c_tags || queryObj.c_sort) {
						queryURL += '?' + new URLSearchParams(queryObj).toString();
					}
					if (queryURL === entrySearchPreviousQueryURL) {
						return;
					}
					if (Object.prototype.hasOwnProperty.call(entrySearchCache, queryURL)) {
						entrySearchPreviousQueryURL = queryURL;
						this.entrySearchDone(entrySearchCache, entrySearchLabel, queryObj.c_query, queryURL);
					} else {
						document.getElementById('js-entry-list').classList.add('opacity-50');
						document.querySelectorAll('.js-entry-search-spin').forEach(el => {
							el.classList.add('animate-pulse');
						});
						axios({
							method: "get",
							url: queryURL,
							headers: { 'X-Requested-With': 'XMLHttpRequest' },
						}).then(response => {
							entrySearchPreviousQueryURL = queryURL;
							entrySearchCache[queryURL] = response.data;
							document.getElementById('js-entry-list').classList.remove('opacity-50');
							this.entrySearchDone(entrySearchCache, entrySearchLabel, queryObj.c_query, queryURL);						}).catch(error => {
							// console.log('Content could not be loaded!')
						})
					}
				}, entrySearchTimeOutDelay);
			});
		});
	}

	entrySearchDone(entrySearchCache, label, query, queryURL) {
		document.getElementById('js-entry-list').innerHTML = entrySearchCache[queryURL];
		document.querySelectorAll('.js-entry-search-spin').forEach(el => {
			el.classList.remove('animate-pulse');
		});
		if (query === '') {
			if (label) {
				label.innerText = label.getAttribute('data-default-label');
			}
			document.querySelectorAll('.js-entry-search-hidden').forEach(elem => {
				elem.classList.remove('hidden');
			});
		} else {
			if (label) {
				label.innerText = label.getAttribute('data-query-label').replace('%s', query);
			}
			document.querySelectorAll('.js-entry-search-hidden').forEach(elem => {
				elem.classList.add('hidden');
			});
		}
	}

	/**
	 * Initialize list filters
	 */
	initListFilters() {
		const filters = document.querySelectorAll('[data-filter-list]');
		if (!filters.length) return;

		const params = new URLSearchParams(window.location.search);
		let filtered = false;

		filters.forEach(filter => {
			const list = document.getElementById(filter.getAttribute('data-filter-list'));
			if (!list) return;

			const form = filter.closest('form:not([data-filter-form]');
			if (form) {
				form.setAttribute('data-filter-form', filter.getAttribute('data-filter-list'));
				if (!list.getAttribute('tabindex')) {
					list.setAttribute('tabindex', '-1');
				}
				form.addEventListener('submit', e => {
					e.preventDefault();
					list.focus();
				})
			}

			const filterProp = filter.getAttribute('data-filter-prop') || 'innerText';
			const noResults = list.getAttribute('data-filter-no-results-id')
				? document.getElementById(list.getAttribute('data-filter-no-results-id'))
				: null;

			filter.addEventListener(filter.nodeName === 'SELECT' || filter.type === 'checkbox' ? 'change' : 'keyup', () => {
				this.filterListItems(filter, list, filterProp, noResults);
			});

			if (filter.name && params.has(filter.name)) {
				filter.value = params.get(filter.name);
				this.filterListItems(filter, list, filterProp, noResults);
				filtered = list;
			}
		})

		if (filtered) {
			filtered.focus({
				preventScroll: true,
			});
			window.scrollTo({
				top: filtered.getBoundingClientRect().top + window.pageYOffset - 150,
				behavior: 'smooth'
			});
		}
	}

	/**
	 * Filter list items
	 *
	 * @param {Element} filter
	 * @param {Element} list
	 * @param {String} filterProp
	 * @param {Element} noResults
	 */
	filterListItems(filter, list, filterProp, noResults) {

		// visibility key
		const visibilityKey = filter.getAttribute('data-filter-vkey') || filterProp;
		const filterValue = filter.type === 'checkbox' ? (filter.checked ? filter.value : '') : filter.value;
		const filterValueAsClass = filter.value.replace(/[^a-z0-9]/gi, '-').toLowerCase();

		// are any filters currently enabled?
		const hasFilters = document.querySelector(
			'[data-filter-list="' + list.id + '"][type="checkbox"]:checked'
			+ ', [data-filter-list="' + list.id + '"][type="radio"]:checked'
		) || Array.from(document.querySelectorAll(
			'[data-filter-list="' + list.id + '"]:not([type="checkbox"]):not([type="radio"])'
		)).some(filter => filter.value.length > 0);

		// are any filters with current visibility key enabled?
		const hasFiltersWithVisibilityKey = document.querySelector(
			'[data-filter-list="' + list.id + '"][data-filter-vkey="' + visibilityKey + '"][type="checkbox"]:checked'
			+ ', [data-filter-list="' + list.id + '"][data-filter-vkey="' + visibilityKey + '"][type="radio"]:checked'
		) || Array.from(document.querySelectorAll(
			'[data-filter-list="' + list.id + '"][data-filter-vkey="' + visibilityKey + '"]:not([type="checkbox"]):not([type="radio"])'
		)).some(filter => filter.value.length > 0);

		// filter items
		list.querySelectorAll(':scope > article, :scope > li').forEach(item => {

			// if there are no filters enabled, show all items
			if (!hasFilters) {
				item.classList.remove('hidden');
				item.removeAttribute('data-filters');
				const group = item.getAttribute('data-filter-group');
				if (group) {
					const label = list.querySelector('[data-filter-group-label="' + group + '"]');
					if (label) {
						label.classList.remove('hidden');
					}
				}
				return;
			}

			// get values and currently applied filters
			const itemValue = filterProp === 'innerText' ? item.innerText : item.getAttribute(filterProp);
			const itemFilters = JSON.parse(item.getAttribute('data-filters') || '{}');

			// if filter is empty/unchecked, remove it from applied filters
			if (!filterValue.length) {
				if (Object.prototype.hasOwnProperty.call(itemFilters, visibilityKey)) {
					itemFilters[visibilityKey] = itemFilters[visibilityKey].filter(v => v !== filterValueAsClass);
					if (!hasFiltersWithVisibilityKey) {
						delete itemFilters[visibilityKey];
					}
				}
			}

			// for text comparison we need to initially clear any applied filters
			if (filterProp === 'innerText') {
				if (!filterValue.length) {
					delete itemFilters[visibilityKey];
				} else {
					itemFilters[visibilityKey] = [];
				}
			}

			// check if value matches filter
			const match = filterValue.length && itemValue.toLowerCase().includes(filter.value.toLowerCase());

			// update applied filters
			if (match) {
				if (!Object.prototype.hasOwnProperty.call(itemFilters, visibilityKey)) {
					itemFilters[visibilityKey] = [];
				}
				if (!itemFilters[visibilityKey].includes(filterValueAsClass)) {
					itemFilters[visibilityKey].push(filterValueAsClass);
				}
			} else if (Object.prototype.hasOwnProperty.call(itemFilters, visibilityKey)) {
				itemFilters[visibilityKey] = itemFilters[visibilityKey].filter(v => v !== filterValueAsClass);
			} else if (hasFiltersWithVisibilityKey) {
				itemFilters[visibilityKey] = [];
			}

			item.setAttribute('data-filters', JSON.stringify(itemFilters));

			// show or hide item: item is shown if it has at least one matching filter in each group
			const show = Object.keys(itemFilters).length > 0 && Object.keys(itemFilters).every(key => {
				return itemFilters[key].length > 0;
			});
			if (show) {
				item.classList.remove('hidden');
			} else {
				item.classList.add('hidden');
			}

			// check if group should be hidden or shown
			const group = item.getAttribute('data-filter-group');
			if (group) {
				const label = list.querySelector('[data-filter-group-label="' + group + '"]');
				if (label) {
					if (list.querySelector('[data-filter-group="' + group + '"]:not(.hidden)')) {
						label.classList.remove('hidden');
					} else {
						label.classList.add('hidden');
					}
				}
			}
		})

		// update URL
		// this.setQueryStringParam(filter.name, filterValue);

		// show or hide the no results element
		if (noResults) {
			noResults.classList.add('hidden');
			if (list.querySelector(':scope > article:not(.hidden), :scope > li:not(.hidden)')) {
				list.classList.remove('hidden');
				return;
			}
			list.classList.add('hidden');
			noResults.classList.remove('hidden');
		}
	}
}


window._site = new Site();
